import PropTypes from 'prop-types'
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

// Component imports
import {
  AssistinoButtonContainer,
  AssistinoIcon,
  AssistinoPopup,
  AssistinoPopupHint,
  AssistinoPopupTitle,
  AssistinoTrialBadge,
  AssistinoTrialTeaser,
  TeaserBody,
  TeaserClose,
  TeaserCta,
  TeaserIconCircle,
  TeaserText,
  TeaserTitle,
  TeaserWandIcon,
} from './assistinoButtonStyles'
import { Footer, FooterContent } from './footer'
import {
  AppHeader,
  HeaderBtn,
  HeaderItem,
  Logo,
  LogoImg,
  LogoNav,
  LogoSmall,
  MobileAppHeader,
  MobileBrandLink,
  MobileLogoImg,
  MobileHeaderBottomRow,
  MobileHeaderTopRow,
  Navbar,
  NavBarBrandBox,
} from './header'
import LanguagesDropdown from './languagesDropdown'
import ProfileMenu from './profileMenu'
import SettingsDropdown from './settingDropdown'
import Sidebar from './sidebar'
import { DrawerOverlay, VerticalMenu } from './sidebar-styles'
import Main from './styles'
import UserCompanyDropdown from './userCompanyDropdown'

// Context, hooks and utilities
import { MenuItemsCounterContext } from '../../contexts/menuItemsCounterContext'
import useAgencyType from '../../hooks/useAgencyType'
import useUnitNaming from '../../hooks/useUnitNaming'

import FullLogo from '../../assets/logos/full-logo-light.png'
import FullLogoDark from '../../assets/logos/full-logo.png'
import useAuthStore from '../../stores/authStore'
// Constants and assets
import { useTheme } from '../../contexts/themeContext'
import { baseLayout } from '../../constants/theme'
import permissions from '../../features/configuration/utils/rolesPermissions'
import { getImageColorFilters } from '../../utils/colorUtilities'
import { MiniLogo } from '../styled'

import { userContext } from '../../contexts/userContext'
import NotificationsCenter from '../../features/notifications/components/notification/NotificationsCenter'
import { createMenuStructure } from './utils/menuConfig'
import ViewportWarning from './viewPortWarning'

const RESPONSIVE_ROUTES = ['']

const isResponsiveRoute = (pathname) =>
  RESPONSIVE_ROUTES.some((route) =>
    route === '/' ? pathname === '/' : pathname.includes(route),
  )

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation()
  const { theme } = useTheme()
  const { pathname } = useLocation()

  const { isAgency, agencyId } = useAuthStore.getState().identity || {}
  const { organizationType } = useAgencyType(agencyId, true, true)
  const { unitsPanelLabel } = useUnitNaming()
  const { menuItemsCounter } = useContext(MenuItemsCounterContext)
  const { state: user } = useContext(userContext)

  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 992)
  const [showChildren, setShowChildren] = useState(
    () => window.innerWidth > 992,
  )
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < baseLayout.mobileBreakpoint,
  )
  const [drawerOpen, setDrawerOpen] = useState(false)

  const showAppShell = showChildren || isResponsiveRoute(pathname)

  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setDrawerOpen((prev) => !prev)
      return
    }

    setCollapsed((prev) => {
      const newCollapsedState = !prev

      if (!newCollapsedState && !showChildren) {
        setShowChildren(true)
      }

      return newCollapsedState
    })
  }, [isMobile, showChildren])

  useEffect(() => {
    let resizeTimer

    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        const isSmallScreen = window.innerWidth < 992
        const isPhone = window.innerWidth < baseLayout.mobileBreakpoint
        setShowChildren(!isSmallScreen)
        setCollapsed(isSmallScreen)
        setIsMobile(isPhone)
        if (!isPhone) setDrawerOpen(false)
      }, 100)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimer)
    }
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!drawerOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [drawerOpen])

  // const menuItems = useMemo(
  //   () =>
  //     createMenuStructure(
  //       t,
  //       isAgency,
  //       organizationType,
  //       menuItemsCounter,
  //       theme,
  //       permissions,
  //     ),
  //   [t, isAgency, organizationType, menuItemsCounter, user?.companyId],
  // )
  const isEmployee = user?.role === 'Employee'

  const menuItems = useMemo(
    () =>
      createMenuStructure(
        t,
        isAgency,
        organizationType,
        menuItemsCounter,
        theme,
        permissions,
        { unitsPanel: unitsPanelLabel },
        isEmployee,
      ),
    [
      t,
      isAgency,
      organizationType,
      menuItemsCounter,
      theme,
      permissions,
      unitsPanelLabel,
      isEmployee,
    ],
  )

  return (
    <div>
      {showAppShell ? (
        <>
          {isMobile ? (
            <MobileHeaderSection toggleSidebar={toggleSidebar} />
          ) : (
            <>
              <NavBarSection
                collapsed={collapsed}
                fullLogo={FullLogo}
                theme={theme}
              />

              <HeaderSection
                collapsed={collapsed}
                language={i18n?.language}
                toggleSidebar={toggleSidebar}
              />
            </>
          )}

          {isMobile && (
            <DrawerOverlay
              open={drawerOpen}
              onClick={closeDrawer}
              aria-hidden="true"
            />
          )}

          <VerticalMenu
            collapsed={collapsed}
            drawerOpen={drawerOpen}
            locale={i18n?.language}
          >
            <Sidebar
              isSidebarExpanded={isMobile ? true : !collapsed}
              menuItems={menuItems}
            />
          </VerticalMenu>

          <Main collapsed={collapsed} locale={i18n?.language}>
            {children}
          </Main>

          <FooterSection
            collapsed={collapsed}
            t={t}
            language={i18n?.language}
          />
        </>
      ) : (
        <ViewportWarning />
      )}
    </div>
  )
}

const NavBarSection = ({ collapsed, fullLogo, theme }) => (
  <NavBarBrandBox $collapsed={collapsed}>
    <LogoNav to="/" $collapsed={collapsed}>
      <Logo>
        {collapsed ? (
          <MiniLogo
            primaryColor={theme.sidebarTextColor}
            secondaryColor={theme.collapsedLogoSecondary}
            height={47}
            width={47}
          />
        ) : (
          <LogoImg
            className="text-center"
            src={fullLogo}
            alt="Full logo"
            style={{
              filter: getImageColorFilters(theme.sidebarTextColor),
            }}
          />
        )}
      </Logo>
      <LogoSmall>
        <MiniLogo height={40} width={40} />
      </LogoSmall>
    </LogoNav>
  </NavBarBrandBox>
)

const HeaderSection = ({ collapsed, language, toggleSidebar }) => (
  <AppHeader collapsed={collapsed} locale={language}>
    <Navbar>
      <div className="d-flex align-items-center">
        <HeaderItem
          type="button"
          className=" mx-3"
          id="vertical-menu-btn"
          onClick={toggleSidebar}
        >
          <HeaderBtn />
        </HeaderItem>
        <UserCompanyDropdown />
      </div>

      <div className="d-flex align-items-center gap-4">
        {/* <SettingsDropdown /> */}
        <LanguagesDropdown />
        {/* <NotificationDropdown /> */}
        <NotificationsCenter />
        <ProfileMenu />
      </div>
    </Navbar>
  </AppHeader>
)
const MobileHeaderSection = ({ toggleSidebar }) => (
  <MobileAppHeader>
    <MobileHeaderTopRow>
      <HeaderItem type="button" id="vertical-menu-btn" onClick={toggleSidebar}>
        <HeaderBtn />
      </HeaderItem>

      <MobileBrandLink to="/">
        <MobileLogoImg src={FullLogoDark} alt="Talentino" />
      </MobileBrandLink>

      <div className="d-flex align-items-center ms-auto">
        <NotificationsCenter />
        <ProfileMenu />
      </div>
    </MobileHeaderTopRow>

    <MobileHeaderBottomRow>
      <UserCompanyDropdown />
      <LanguagesDropdown />
    </MobileHeaderBottomRow>
  </MobileAppHeader>
)

const LazyAssistinoChatPopup = lazy(
  () => import('../../features/Assistino/components/AssistinoChatPopup.jsx'),
)

// keeps the dismiss button from also triggering the card's own key handler
const stopEventPropagation = (e) => e.stopPropagation()

const ASSISTINO_TRIAL_TEASER_KEY = 'assistino-trial-teaser-dismissed'
const ASSISTINO_TRIAL_TEASER_DELAY = 1500

// TODO: remove this when assistino in prod is fixed
const isProductionEnv = import.meta.env.VITE_ENV === 'prod'

const isTrialTeaserDismissed = () => {
  try {
    return localStorage.getItem(ASSISTINO_TRIAL_TEASER_KEY) === 'true'
  } catch {
    return false
  }
}

const rememberTrialTeaserDismissal = () => {
  try {
    localStorage.setItem(ASSISTINO_TRIAL_TEASER_KEY, 'true')
  } catch {}
}

/**
 * Footer section component
 */
const FooterSection = ({ collapsed, t, language }) => {
  const location = useLocation()
  const isAssistino = location.pathname.startsWith('/assistino')
  const { theme } = useTheme()
  const [showAssistinoPopup, setShowAssistinoPopup] = useState(false)
  const [showTrialTeaser, setShowTrialTeaser] = useState(false)

  const showAssistinoLauncher = !isAssistino && !isProductionEnv

  useEffect(() => {
    if (!showAssistinoLauncher || isTrialTeaserDismissed()) return undefined

    const timer = setTimeout(
      () => setShowTrialTeaser(true),
      ASSISTINO_TRIAL_TEASER_DELAY,
    )

    return () => clearTimeout(timer)
  }, [showAssistinoLauncher])

  const dismissTrialTeaser = useCallback(() => {
    setShowTrialTeaser(false)
    rememberTrialTeaserDismissal()
  }, [])

  const handleTrialTeaserClose = useCallback(
    (e) => {
      e.stopPropagation()
      dismissTrialTeaser()
    },
    [dismissTrialTeaser],
  )

  const handleAssistinoClick = () => {
    dismissTrialTeaser()
    setShowAssistinoPopup((open) => !open)
  }

  const handleAssistinoClose = useCallback(
    () => setShowAssistinoPopup(false),
    [],
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleAssistinoClick()
    }
  }

  return (
    <>
      <Footer collapsed={collapsed} isAssistino={isAssistino}>
        {t('layout.talentinoAI')}{' '}
        <FooterContent>
          © {new Date().getFullYear()}. {t('layout.allRightsReserved')}.
        </FooterContent>
      </Footer>

      {showAssistinoLauncher && (
        <AssistinoButtonContainer
          theme={theme}
          locale={language}
          onClick={handleAssistinoClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-label={t('layout.assistino')}
        >
          <AssistinoIcon locale={language} aria-hidden="true" />
          <AssistinoTrialBadge theme={theme} locale={language}>
            {t('layout.assistinoTrialBadge')}
          </AssistinoTrialBadge>
          <AssistinoPopup
            theme={theme}
            locale={language}
            className="assistino-popup"
          >
            <AssistinoPopupTitle>{t('layout.assistino')}</AssistinoPopupTitle>
            <AssistinoPopupHint>
              {t('layout.assistinoTrialMessage')}
            </AssistinoPopupHint>
          </AssistinoPopup>
        </AssistinoButtonContainer>
      )}

      {showAssistinoLauncher && showTrialTeaser && (
        <AssistinoTrialTeaser
          theme={theme}
          locale={language}
          role="button"
          tabIndex={0}
          onClick={handleAssistinoClick}
          onKeyDown={handleKeyDown}
          aria-label={`${t('layout.assistinoTrialTitle')} - ${t(
            'layout.assistinoTrialMessage',
          )}`}
        >
          <TeaserIconCircle theme={theme}>
            <TeaserWandIcon theme={theme} aria-hidden="true" />
          </TeaserIconCircle>

          <TeaserBody>
            <TeaserTitle theme={theme}>
              {t('layout.assistinoTrialTitle')}
            </TeaserTitle>
            <TeaserText theme={theme}>
              {t('layout.assistinoTrialMessage')}
            </TeaserText>
            <TeaserCta theme={theme} locale={language}>
              {t('layout.assistinoTrialCta')}
            </TeaserCta>
          </TeaserBody>

          <TeaserClose
            theme={theme}
            locale={language}
            type="button"
            onClick={handleTrialTeaserClose}
            onKeyDown={stopEventPropagation}
            aria-label={t('layout.assistinoTrialDismiss')}
          >
            ×
          </TeaserClose>
        </AssistinoTrialTeaser>
      )}

      {showAssistinoLauncher && (
        <Suspense fallback={null}>
          <LazyAssistinoChatPopup
            show={showAssistinoPopup}
            onClose={handleAssistinoClose}
            locale={language}
          />
        </Suspense>
      )}
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

// PropTypes for component sections
NavBarSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  fullLogo: PropTypes.string.isRequired,
}

HeaderSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  language: PropTypes.string,
  toggleSidebar: PropTypes.func.isRequired,
}

MobileHeaderSection.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
}

FooterSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
}

export default Layout
